import React, { useState, useEffect } from 'react';
import 'react-data-grid/lib/styles.css';
import MapSidebar from '../modals/MapSidebar';
import PropertySummaryModal from '../modals/PropertySummaryModal';
import PropertyDetailsModal from '../modals/PropertyDetailsModal';
import ZillowModal from '../modals/ZillowModal';
import ResidentDetailsModal from '../modals/ResidentDetailsModal';
import ListingsTable from './components/ListingsTable';
import PaginationControls from './components/PaginationControls';
import PerformanceIndicator from './components/PerformanceIndicator';
import { useListingsTable } from './hooks/useListingsTable';
import { useAutoRefresh } from './hooks/useAutoRefresh';
import { handleAddressClick, handleZillowClick, handlePropertyDetailsClick } from './utils/addressHandlers';
import { chartStyles } from './styles/ChartStyles';
import { listingsGridStyles } from './styles/ListingsGridStyles';
import { useRole, ROLES } from '../../contexts/RoleContext';
import { SubscriptionModal } from '../modals';

const ListingsGrid = ({ listings, loading, pagination, onPageChange }) => {
  // Role-based access control
  const { hasRole, isPro } = useRole();
  
  // Modal states
  const [mapSidebarOpen, setMapSidebarOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [propertyDetailsModalOpen, setPropertyDetailsModalOpen] = useState(false);
  const [selectedPropertyForDetails, setSelectedPropertyForDetails] = useState(null);
  const [zillowModalOpen, setZillowModalOpen] = useState(false);
  const [zillowUrl, setZillowUrl] = useState('');
  const [zillowPropertyAddress, setZillowPropertyAddress] = useState('');
  const [residentModalOpen, setResidentModalOpen] = useState(false);
  const [selectedResidentAddress, setSelectedResidentAddress] = useState('');
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  // Auto-refresh state - now managed by the useAutoRefresh hook

  // Use custom hook for table logic
  const {
    fetchedPropertyData,
    expandedRows,
    sortConfig,
    displayedListings,
    requestQueue,
    isAutoRefreshing,
    toggleRowExpansion,
    requestSort,
    handleRowsPerPageChange,
    setIsAutoRefreshing
  } = useListingsTable(listings, pagination, onPageChange);

  // Modal handlers
  const closeMapSidebar = () => {
    setMapSidebarOpen(false);
  };

  const closeZillowModal = () => {
    setZillowModalOpen(false);
    // Clear the Zillow URL after the modal animation completes
    setTimeout(() => {
      setZillowUrl('');
      setZillowPropertyAddress('');
    }, 300);
  };

  const closePropertyDetailsModal = () => {
    setPropertyDetailsModalOpen(false);
    setSelectedPropertyForDetails(null);
  };

  const closeResidentModal = () => {
    setResidentModalOpen(false);
    setSelectedResidentAddress('');
  };

  const handleSubscriptionModal = () => {
    setSubscriptionModalOpen(true);
  };

  const handleSummaryClick = (listing) => {
    setSelectedProperty(listing);
    setSummarizing(false);
    setSummaryModalOpen(true);
  };

  // Address click handler
  const onAddressClick = (e, address, city, state) => {
    handleAddressClick(e, address, city, state, setSelectedAddress, setMapSidebarOpen);
  };

  // Zillow click handler
  const onZillowClick = (e, address, city, state) => {
    handleZillowClick(e, address, city, state, setZillowUrl, setZillowPropertyAddress, setZillowModalOpen);
  };

  // Property details click handler
  const onPropertyDetailsClick = (e, listing) => {
    handlePropertyDetailsClick(e, listing, setSelectedPropertyForDetails, setPropertyDetailsModalOpen);
  };

  // Resident details click handler
  const handleResidentDetailsClick = (listing) => {
    setSelectedResidentAddress(listing.address);
    setResidentModalOpen(true);
  };

  // Use the centralized auto-refresh hook instead of duplicating polling logic
  const { 
    isAutoRefreshing: hookIsAutoRefreshing
  } = useAutoRefresh(() => {
    // Callback to refresh data when scraping is detected
    onPageChange(pagination.currentPage, pagination.pageSize);
  }, 10000); // Check every 10 seconds to reduce API calls

  // Sync the hook's auto-refresh state with the listings table state
  useEffect(() => {
    setIsAutoRefreshing(hookIsAutoRefreshing);
  }, [hookIsAutoRefreshing, setIsAutoRefreshing]);

  return (
    <div className={listingsGridStyles.container}>
      {/* Auto-refresh indicator */}
      {isAutoRefreshing && (
        <div className={listingsGridStyles.autoRefreshIndicator}>
          <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>Auto-refreshing data...</span>
        </div>
      )}

      {/* Performance Indicator - Admin Only */}
      {hasRole(ROLES.ADMIN) && (
        <PerformanceIndicator 
          fetchedPropertyData={fetchedPropertyData}
          totalProperties={listings?.length || 0}
        />
      )}

      {/* Add the chart styles */}
      <style>{chartStyles}</style>
      
      {/* Loading state */}
      {loading ? (
        <div className={listingsGridStyles.loadingContainer}>
          <div className={listingsGridStyles.loadingSpinner}></div>
          <p className={listingsGridStyles.loadingText}>Loading foreclosure listings...</p>
        </div>
      ) : (
        <>
          {/* Main content */}
          <ListingsTable
            displayedListings={displayedListings}
            fetchedPropertyData={fetchedPropertyData}
            expandedRows={expandedRows}
            requestQueue={requestQueue}
            sortConfig={sortConfig}
            onRequestSort={requestSort}
            onToggleExpansion={toggleRowExpansion}
            onAddressClick={onAddressClick}
            onZillowClick={onZillowClick}
            onSummaryClick={handleSummaryClick}
            onPropertyDetailsClick={onPropertyDetailsClick}
            onResidentDetailsClick={handleResidentDetailsClick}
            loading={loading}
          />

          {/* Pagination controls */}
          {pagination && pagination.totalCount > 0 && (
            <PaginationControls
              pagination={pagination}
              onPageChange={onPageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
          
          {/* Property summary modal */}
          {selectedProperty && (
            <PropertySummaryModal
              isOpen={summaryModalOpen}
              property={selectedProperty}
              loading={summarizing}
              onClose={() => setSummaryModalOpen(false)}
            />
          )}
          
          {/* Map sidebar */}
          <MapSidebar
            isOpen={mapSidebarOpen}
            address={selectedAddress}
            onClose={closeMapSidebar}
          />
          
          {/* Zillow modal */}
          <ZillowModal
            isOpen={zillowModalOpen}
            zillowUrl={zillowUrl}
            propertyAddress={zillowPropertyAddress}
            onClose={closeZillowModal}
          />
          
          {/* Property Details modal */}
          {selectedPropertyForDetails && (
            <PropertyDetailsModal
              isOpen={propertyDetailsModalOpen}
              property={selectedPropertyForDetails}
              propertyData={fetchedPropertyData[selectedPropertyForDetails.id || selectedPropertyForDetails.property_id]}
              onClose={closePropertyDetailsModal}
              isProUser={isPro()}
              onSubscriptionModal={handleSubscriptionModal}
            />
          )}
          
          {/* Resident Details modal */}
          <ResidentDetailsModal
            isOpen={residentModalOpen}
            address={selectedResidentAddress}
            onClose={closeResidentModal}
          />

          {/* Subscription modal */}
          <SubscriptionModal
            open={subscriptionModalOpen}
            onClose={() => setSubscriptionModalOpen(false)}
            onUpgrade={() => setSubscriptionModalOpen(false)}
          />
        </>
      )}
    </div>
  );
};

export default ListingsGrid;